import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import PropTypes from 'prop-types';
import React, { useState, useRef, useEffect } from 'react';
import { Button, Modal, ModalBody, ModalFooter } from 'reactstrap';
import Loader from './loader';
const width = 1024;
const height = 720;
const Snapper = _ref => {
  let onSave = _ref.onSave;
  const _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    canvasSaved = _useState2[0],
    setCanvasSaved = _useState2[1];
  const _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    loaded = _useState4[0],
    setLoaded = _useState4[1];
  const _useState5 = useState(false),
    _useState6 = _slicedToArray(_useState5, 2),
    cameraUnavailable = _useState6[0],
    setCameraUnavailable = _useState6[1];
  const _useState7 = useState(false),
    _useState8 = _slicedToArray(_useState7, 2),
    showModal = _useState8[0],
    setShowModal = _useState8[1];
  const _useState9 = useState(false),
    _useState0 = _slicedToArray(_useState9, 2),
    imageCapture = _useState0[0],
    setImageCapture = _useState0[1];

  // Use refs instead of global variables
  const videoRef = useRef(null);
  const localStreamRef = useRef(null);
  const canvasRef = useRef(null);
  const streamCamVideo = async () => {
    try {
      try {
        localStreamRef.current = await global.navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: {
              exact: 'environment'
            },
            width,
            height
          }
        });
      } catch (error) {
        localStreamRef.current = await global.navigator.mediaDevices.getUserMedia({
          video: {
            width,
            height
          }
        });
      }

      // Wait for the video element to be ready, then attach stream
      setTimeout(() => {
        if (videoRef.current && localStreamRef.current) {
          videoRef.current.srcObject = localStreamRef.current;
          videoRef.current.onloadedmetadata = () => {
            if (videoRef.current) {
              videoRef.current.play().then(() => {
                setLoaded(true);
              }).catch(() => {
                setCameraUnavailable(true);
              });
            }
          };
          videoRef.current.onerror = () => {
            setCameraUnavailable(true);
          };
        }
      }, 200);
    } catch (_ref2) {
      let name = _ref2.name;
      let message = _ref2.message;
      // This will trigger the loader 'failed' condition and show alert text.
      setCameraUnavailable(true);
    }
  };

  // Effect to handle video stream when modal opens
  useEffect(() => {
    if (showModal && localStreamRef.current && videoRef.current && !loaded) {
      videoRef.current.srcObject = localStreamRef.current;
      videoRef.current.onloadedmetadata = () => {
        if (videoRef.current) {
          videoRef.current.play().then(() => {
            setLoaded(true);
          }).catch(() => {
            setCameraUnavailable(true);
          });
        }
      };
    }
  }, [showModal, loaded]);
  const stopStreamingCamVideo = () => {
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.src = '';
    }
    if (localStreamRef.current) {
      localStreamRef.current.getTracks()[0].stop();
    }
    setLoaded(false);
  };
  const openCameraModal = () => {
    setCameraUnavailable(false);
    setShowModal(true);
    setLoaded(false); // Start with loading state
    streamCamVideo();
  };
  const closeCameraModal = () => {
    if (loaded) {
      stopStreamingCamVideo();
      setLoaded(false);
      setCanvasSaved(false);
    }
    setShowModal(false);
  };
  const snapPicture = () => {
    if (videoRef.current) {
      videoRef.current.pause();
      canvasRef.current = global.document.createElement('canvas');
      canvasRef.current.width = videoRef.current.videoWidth;
      canvasRef.current.height = videoRef.current.videoHeight;
      canvasRef.current.getContext('2d').drawImage(videoRef.current, 0, 0, canvasRef.current.width, canvasRef.current.height);
      setCanvasSaved(true);
    }
  };
  const save = () => {
    if (canvasRef.current) {
      canvasRef.current.toBlob(newBlobData => {
        closeCameraModal();
        onSave([newBlobData]);
      }, 'image/jpeg', 1);
    }
  };
  const retakePicture = () => {
    if (videoRef.current) {
      videoRef.current.play();
    }
    setImageCapture('');
    setCanvasSaved(false);
  };

  // Cleanup effect to stop stream when component unmounts
  useEffect(() => () => {
    if (localStreamRef.current && typeof localStreamRef.current.getTracks === 'function') {
      localStreamRef.current.getTracks().forEach(track => track.stop());
    }
  }, []);
  const failedMessage = 'Unable to load camera. Check your camera\'s connection or your browser permissions and try again.';
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Button, {
    block: true,
    style: {
      marginTop: '10px'
    },
    onClick: openCameraModal
  }, "Take Picture"), /*#__PURE__*/React.createElement(Modal, {
    isOpen: showModal,
    style: {
      maxWidth: '835px'
    }
  }, /*#__PURE__*/React.createElement(ModalBody, null, /*#__PURE__*/React.createElement("div", {
    "data-testid": "snapper-modal-container",
    id: "container"
  }, !loaded && !cameraUnavailable && /*#__PURE__*/React.createElement(Loader, {
    loaded: false,
    failedMessage: ""
  }), cameraUnavailable && /*#__PURE__*/React.createElement(Loader, {
    loaded: false,
    failed: true,
    failedMessage: failedMessage
  }), /*#__PURE__*/React.createElement("video", {
    ref: videoRef,
    "data-testid": "snapper-video",
    width: "100%",
    autoPlay: true,
    playsInline: true,
    muted: true,
    id: "videoElement",
    style: {
      display: loaded ? 'block' : 'none'
    }
  }, /*#__PURE__*/React.createElement("track", {
    kind: "captions",
    label: "live user webcam"
  })))), /*#__PURE__*/React.createElement(ModalFooter, null, /*#__PURE__*/React.createElement("div", {
    className: "button-group mr-auto"
  }, /*#__PURE__*/React.createElement(Button, {
    disabled: !loaded || canvasSaved,
    onClick: () => snapPicture()
  }, "Snap Picture"), /*#__PURE__*/React.createElement(Button, {
    disabled: !canvasSaved,
    onClick: () => retakePicture(),
    style: {
      marginLeft: '5px'
    }
  }, "Retake"), /*#__PURE__*/React.createElement(Button, {
    disabled: !canvasSaved,
    onClick: () => save(),
    style: {
      marginLeft: '5px'
    }
  }, "Save")), imageCapture ? /*#__PURE__*/React.createElement(Button, {
    onClick: () => closeCameraModal(),
    className: "mr-auto"
  }, "Save") : null, /*#__PURE__*/React.createElement(Button, {
    onClick: () => closeCameraModal(),
    className: "t-am-cancel"
  }, "Cancel"))));
};
Snapper.propTypes = {
  onSave: PropTypes.func.isRequired
};
export { Snapper as TestSnapper };
export default Snapper;